import dayjs from 'dayjs';
import { X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CreateObjectModalProps {
  onClose: () => void;
  onCreateObject: (objectData: any) => void;
  data: { position: { x: number; y: number }; detectionDate: string; chessBoard: { grid_cols: string[]; grid_rows: string[] }; position_description: string };
  isLoading?: boolean;
}

const CreateObjectModal: React.FC<CreateObjectModalProps> = ({
  onClose,
  onCreateObject,
  data,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [objectType, setObjectType] = useState('');
  const [description, setDescription] = useState('CHECK');
  const [status, setStatus] = useState('real_vs_plan_dev');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const objectData: any = {
      type: objectType,
      description: description,
      status,
      position: data.position,
      confidence: 1.0,
      detectionDate: data.detectionDate,
      grid_cols: data.chessBoard.grid_cols,
      grid_rows: data.chessBoard.grid_rows,
      position_description: data.position_description,
      chessboard_id: `[${data.chessBoard.grid_cols.join(',')}],[${data.chessBoard.grid_rows.join('-')}]`,
    };

    if (status === 'real_vs_plan_dev') {
      objectData.group_id = 'Deviation';
    }

    onCreateObject(objectData);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('progress_report.create_object')}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('progress_report.type')}
            </label>
            <input
              value={objectType}
              onChange={(e) => setObjectType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('progress_report.status')}
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={true}
            >
              <option value="real_vs_plan_dev">{t('progress_report.real_vs_plan_dev')}</option>
            </select>
          </div>


          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('progress_report.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Description`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="text-sm text-gray-500">
            {t('progress_report.position')}: ({Math.round(data.position.x)}, {Math.round(data.position.y)})
          </div>

          <div className="text-sm text-gray-500">
            {t('progress_report.detection_date')}: {dayjs.utc(data.detectionDate).format('YYYY-MM-DD HH:mm')}
          </div>

          <div className="text-sm text-gray-500">
            {t('progress_report.chessboard')}:[{data.chessBoard.grid_cols.join(',')}],[{data.chessBoard.grid_rows.join('-')}]
          </div>

          <div className="text-sm text-gray-500">
            {t('progress_report.position_description')}: {data.position_description}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
              disabled={isLoading || !objectType || !status}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('progress_report.creating')}
                </>
              ) : (
                t('progress_report.create_object')
              )}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
};

export default CreateObjectModal;
